import { AttachFile, DeleteOutlineOutlined } from '@mui/icons-material';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';

import { useImageValidator } from 'shared/hooks/useImageValidator';
import { Seller } from 'shared/types/Sellers';
import { Modal } from 'shared/ui/Modal';
import { SellerLogo } from 'shared/ui/SellerLogo';

interface EditBrandLogoModalProps {
  handleChangeLogo: (value: File) => void;
  handleCloseModal: () => void;
  titleModal: string;
  isModalOpen: boolean;
  isUpdatingBrand: boolean;
  approveNameButton: string;
  seller: Seller;
  error?: boolean | undefined;
}

export const EditBrandLogoModal = ({
  handleChangeLogo,
  handleCloseModal,
  titleModal,
  isModalOpen,
  isUpdatingBrand,
  approveNameButton,
  seller,
  error,
}: EditBrandLogoModalProps) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState<File | null>(null);
  const [typeForUploading, setTypeForUploading] = useState('file');

  const {
    validate: validateImage,
    error: errorImage,
    previewUrl,
    clear: clearImage,
  } = useImageValidator({
    allowedTypes: ['image/png', 'image/jpeg'],
    maxFileSizeMB: 1,
    minWidth: 128,
    minHeight: 128,
    maxWidth: 512,
    maxHeight: 512,
    requireSquare: true,
    squareTolerance: 0.05,
  });

  const resetImage = () => {
    setFile(null);
    fileInputRef.current.value = null;
    clearImage();
  };

  const handleSubmitModal = () => {
    if (file) {
      handleChangeLogo?.(file);
    }
  };

  const handleTextFieldClick = () => {
    resetImage();
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const isValid = await validateImage(file);
      if (isValid) {
        setFile(file);
      } else {
        resetImage();
      }
    }
  };

  const handleDeleteFile = (event: any) => {
    event.stopPropagation();
    resetImage();
  };

  return (
    <Modal
      disabled={error || isUpdatingBrand || !file}
      isModalOpen={isModalOpen}
      isLoading={isUpdatingBrand}
      handleCloseModal={handleCloseModal}
      handleSubmitModal={handleSubmitModal}
      approveNameButton={approveNameButton}
    >
      <Box>
        <Typography typography="openSans.h4">{titleModal}</Typography>
        <Typography typography="openSans.body2" sx={{ margin: '12px 0 20px' }}>
          {`Please, select an image (PNG or JPG) to upload`}
        </Typography>
        <input
          type="file"
          accept="image/png, image/jpeg"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
        <TextField
          onFocus={() => setTypeForUploading('file')}
          color="secondary"
          sx={{
            input: { cursor: 'pointer' },
            '.MuiInputBase-input': {
              color: file ? 'primary.500' : 'inherit',
            },
          }}
          error={!!errorImage}
          placeholder="Logo"
          fullWidth
          label="Logo"
          onClick={handleTextFieldClick}
          InputProps={{
            readOnly: true,
            startAdornment: file && (
              <AttachFile sx={{ color: 'primary.500', marginRight: 1 }} />
            ),
            endAdornment: file && (
              <IconButton aria-label="delete" onClick={handleDeleteFile}>
                <DeleteOutlineOutlined sx={{ color: 'primary.main' }} />
              </IconButton>
            ),
          }}
          value={file ? file.name : ''}
        />
        {errorImage && (
          <Typography
            typography="openSans.body2"
            color="error"
            sx={{ marginTop: '10px' }}
          >
            {errorImage}
          </Typography>
        )}
        <Box
          sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}
        >
          <SellerLogo logo={previewUrl} size={100} seller={seller} />
        </Box>
      </Box>
    </Modal>
  );
};
